/**
 * Utility functions for risk gauge calculations
 * Extracted from the main component to improve maintainability
 */


// Risk gauge configuration constants
const RISK_GAUGE_CONFIG = {
  maxWidth: 301,
  maxHeight: 215,
  defaultSize: 301,
  defaultStrokeWidth: 14,
  innerRadiusOffset: 30,
  triangleSize: 8,
  triangleOffset: 15,
  tickMajorLength: 8,
  tickMinorLength: 4,
  tickDivisions: 40,
};

// Calculate dimensions based on configuration
export const calculateDimensions = (size: number, strokeWidth: number) => {
  const actualSize = Math.min(size, RISK_GAUGE_CONFIG.maxWidth);
  const actualHeight = Math.min(actualSize * 0.6, RISK_GAUGE_CONFIG.maxHeight);

  const outerRadius = (actualSize - strokeWidth) / 2;
  const innerRadius = outerRadius - RISK_GAUGE_CONFIG.innerRadiusOffset;

  return {
    actualSize,
    actualHeight,
    outerRadius,
    innerRadius,
  };
};

// Calculate semicircle path data
export const calculateSemicirclePaths = (dimensions: ReturnType<typeof calculateDimensions>, strokeWidth: number) => {
  const { actualSize, actualHeight, outerRadius, innerRadius } = dimensions;

  const outerPath = `M ${strokeWidth/2} ${actualHeight - strokeWidth/2} A ${outerRadius} ${outerRadius} 0 0 1 ${actualSize - strokeWidth/2} ${actualHeight - strokeWidth/2}`;
  const innerPath = `M ${strokeWidth/2 + RISK_GAUGE_CONFIG.innerRadiusOffset} ${actualHeight - strokeWidth/2} A ${innerRadius} ${innerRadius} 0 0 1 ${actualSize - strokeWidth/2 - RISK_GAUGE_CONFIG.innerRadiusOffset} ${actualHeight - strokeWidth/2}`;

  return { outerPath, innerPath };
};

// Calculate stroke dash values
export const calculateStrokeDash = (radius: number, percentage: number) => {
  const semicircleLength = Math.PI * radius;
  const offset = semicircleLength - (percentage / 100) * semicircleLength;
  return { semicircleLength, offset };
};

// Generate tick marks data
export const generateTickMarks = (dimensions: ReturnType<typeof calculateDimensions>, strokeWidth: number) => {
  const { actualSize, actualHeight, outerRadius, innerRadius } = dimensions;

  const ticks = [];
  const tickRadius = (outerRadius + innerRadius) / 2;

  for (let i = 0; i <= RISK_GAUGE_CONFIG.tickDivisions; i++) {
    const angle = (i * Math.PI) / RISK_GAUGE_CONFIG.tickDivisions;
    const isMajorTick = i % 4 === 0;
    const tickLength = isMajorTick ? RISK_GAUGE_CONFIG.tickMajorLength : RISK_GAUGE_CONFIG.tickMinorLength;
    const opacity = isMajorTick ? 0.3 : 0.15;

    const innerTickRadius = tickRadius - tickLength / 2;
    const outerTickRadius = tickRadius + tickLength / 2;

    const x1 = actualSize / 2 + Math.cos(Math.PI - angle) * innerTickRadius;
    const y1 = actualHeight - strokeWidth / 2 - Math.sin(Math.PI - angle) * innerTickRadius;
    const x2 = actualSize / 2 + Math.cos(Math.PI - angle) * outerTickRadius;
    const y2 = actualHeight - strokeWidth / 2 - Math.sin(Math.PI - angle) * outerTickRadius;

    ticks.push({
      key: i,
      x1,
      y1,
      x2,
      y2,
      opacity,
    });
  }

  return ticks;
};

// Calculate triangle position and rotation
export const calculateTriangleTransform = (
  angle: number,
  dimensions: ReturnType<typeof calculateDimensions>,
  strokeWidth: number
) => {
  const { actualSize, actualHeight, innerRadius } = dimensions;

  const x = actualSize / 2 + Math.cos(angle) * (innerRadius - RISK_GAUGE_CONFIG.triangleOffset);
  const y = actualHeight - strokeWidth / 2 - Math.sin(angle) * (innerRadius - RISK_GAUGE_CONFIG.triangleOffset);

  // Convert angle to degrees and adjust so triangle tip points radially outward
  const angleDegrees = (angle * 180) / Math.PI;
  const rotation = -angleDegrees - 90;

  return { x, y, rotation };
};

// Generate triangle points
export const generateTrianglePoints = () => {
  const triangleSize = RISK_GAUGE_CONFIG.triangleSize;
  return `0,${triangleSize} -${triangleSize},-${triangleSize} ${triangleSize},-${triangleSize}`;
};
